import React, { useState } from 'react';
import { Calendar as CalendarIcon, Clock, CheckCircle, ArrowRight, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    queries: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);

  // Time slots: 4:00 PM, 4:30 PM, 5:00 PM, 5:30 PM (20 min + 10 min buffer)
  const timeSlots = [
    { id: 1, time: '4:00 PM', value: '16:00' },
    { id: 2, time: '4:30 PM', value: '16:30' },
    { id: 3, time: '5:00 PM', value: '17:00' },
    { id: 4, time: '5:30 PM', value: '17:30' },
  ];

  // Check if date is Tuesday (2) or Thursday (4)
  const isTuesdayOrThursday = (date) => {
    const day = date.getDay();
    return day === 2 || day === 4;
  };

  // Disable dates that are not Tuesday/Thursday or in the past
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || !isTuesdayOrThursday(date);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null); // Reset slot when date changes
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedSlot) {
      toast({
        title: 'Missing Information',
        description: 'Please select a date and time slot.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        date: selectedDate.toISOString().split('T')[0],
        time: selectedSlot.value,
        timeDisplay: selectedSlot.time,
        dayOfWeek: selectedDate.toLocaleDateString('en-US', { weekday: 'long' }),
      };

      const response = await axios.post(`${API}/bookings`, bookingData);

      if (response.data) {
        setBookingConfirmed(true);
        toast({
          title: 'Booking Confirmed!',
          description: `Your consultation is scheduled for ${selectedDate.toLocaleDateString()} at ${selectedSlot.time}`,
        });
      }
    } catch (error) {
      toast({
        title: 'Booking Failed',
        description: error.response?.data?.detail || 'Something went wrong. Please try again or call us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (date) => {
    return date ? date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }) : '';
  };

  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="border-2 border-green-200 shadow-2xl">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-green-600" size={48} />
                </div>
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  Consultation Booked Successfully!
                </h1>
                <div className="bg-blue-50 rounded-xl p-6 mb-6">
                  <p className="text-lg text-gray-700 mb-2">
                    <strong>Date:</strong> {formatDate(selectedDate)}
                  </p>
                  <p className="text-lg text-gray-700 mb-2">
                    <strong>Time:</strong> {selectedSlot.time}
                  </p>
                  <p className="text-lg text-gray-700">
                    <strong>Duration:</strong> 20 minutes
                  </p>
                </div>
                <p className="text-gray-600 mb-8">
                  A confirmation email has been sent to <strong>{formData.email}</strong>.<br />
                  We'll also send you a reminder before your consultation.
                </p>
                <div className="space-y-4">
                  <Button 
                    onClick={() => window.location.href = '/'}
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 rounded-xl"
                  >
                    Back to Home
                  </Button>
                  <p className="text-sm text-gray-600">
                    Need to reschedule? Call us at <a href="tel:9082738204" className="text-blue-700 font-medium">+91 9082738204</a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-teal-50 pt-32 pb-20">
      {/* Hero Section */}
      <section className="container mx-auto px-4 mb-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-6 py-2 rounded-full text-sm font-medium mb-6">
            <CalendarIcon size={16} />
            Free Consultation
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Book Your Free Consultation
          </h1>
          <p className="text-xl text-gray-600">
            Schedule a 20-minute session with our expert chartered accountants. 
            Available on Tuesdays and Thursdays from 4:00 PM to 6:00 PM.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calendar & Time Slots */}
            <div className="space-y-6">
              {/* Calendar Card */}
              <Card className="border-2 border-blue-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-blue-700 to-teal-600 text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2">
                    <CalendarIcon size={24} />
                    Select a Date
                  </CardTitle>
                  <p className="text-sm text-blue-100">Choose from available Tuesdays and Thursdays</p>
                </CardHeader>
                <CardContent className="p-6">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={isDateDisabled}
                    className="rounded-md border mx-auto"
                  />
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <AlertCircle className="inline mr-2" size={16} />
                      Consultations are available only on <strong>Tuesdays</strong> and <strong>Thursdays</strong>
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Time Slots Card */}
              {selectedDate && (
                <Card className="border-2 border-teal-200 shadow-xl animate-in fade-in duration-300">
                  <CardHeader className="bg-gradient-to-r from-teal-600 to-blue-700 text-white rounded-t-lg">
                    <CardTitle className="flex items-center gap-2">
                      <Clock size={24} />
                      Select a Time Slot
                    </CardTitle>
                    <p className="text-sm text-teal-100">
                      {formatDate(selectedDate)}
                    </p>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-2 gap-4">
                      {timeSlots.map((slot) => (
                        <button
                          key={slot.id}
                          onClick={() => handleSlotSelect(slot)}
                          className={`p-4 rounded-xl font-medium transition-all duration-300 ${
                            selectedSlot?.id === slot.id
                              ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg scale-105'
                              : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-orange-400 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Clock size={18} />
                            {slot.time}
                          </div>
                          <div className="text-xs mt-1 opacity-75">20 mins</div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Booking Details Form */}
            <div>
              <Card className="border-2 border-blue-200 shadow-xl">
                <CardHeader className="bg-gradient-to-r from-blue-700 to-blue-900 text-white rounded-t-lg">
                  <CardTitle>Your Details</CardTitle>
                  <p className="text-sm text-blue-100">Please provide your information</p>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter your full name"
                        className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your.email@example.com"
                        className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 XXXXX XXXXX"
                        className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="queries" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Queries *
                      </label>
                      <Textarea
                        id="queries"
                        name="queries"
                        required
                        value={formData.queries}
                        onChange={handleInputChange}
                        placeholder="Please describe what you'd like to discuss during the consultation..."
                        rows={5}
                        className="border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                      />
                    </div>

                    {/* Summary */}
                    {selectedDate && selectedSlot && (
                      <div className="bg-gradient-to-r from-teal-50 to-blue-50 border-2 border-teal-200 rounded-xl p-4">
                        <h4 className="font-bold text-gray-900 mb-2">Booking Summary:</h4>
                        <p className="text-sm text-gray-700">
                          📅 {formatDate(selectedDate)}<br />
                          🕐 {selectedSlot.time} (20 minutes)<br />
                          📧 Confirmation will be sent to your email
                        </p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting || !selectedDate || !selectedSlot}
                      className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        'Booking...'
                      ) : (
                        <>
                          Confirm Booking
                          <ArrowRight className="ml-2" size={20} />
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-gray-600 text-center">
                      By booking, you agree to receive consultation confirmation and reminders via email and SMS.
                    </p>
                  </form>
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="mt-6 border-2 border-orange-200 shadow-xl bg-gradient-to-br from-orange-50 to-white">
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="text-orange-600" size={20} />
                    What to Expect
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>✓ 20-minute one-on-one consultation</li>
                    <li>✓ Expert guidance from qualified CAs</li>
                    <li>✓ Personalized solutions for your needs</li>
                    <li>✓ No obligation, completely free</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
